import * as React from 'react';
import { AlertTriangle, Database } from 'lucide-react';

interface Props {
  children?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  public render() {
    if (this.state.hasError) {
      let isPermissionError = false;
      try {
        if (this.state.error?.message) {
          const parsed = JSON.parse(this.state.error.message);
          if (parsed.error && parsed.error.includes('Missing or insufficient permissions')) {
            isPermissionError = true;
          }
        }
      } catch (e) {
        // Not a JSON error
      }

      if (isPermissionError) {
        return (
          <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center shadow-2xl">
              <div className="w-32 h-32 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6 ring-1 ring-red-500/20">
                <Database className="text-red-500 w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Database Access Denied</h2>
              <p className="text-zinc-400 mb-6 text-sm leading-relaxed">
                Your application is trying to access Firestore, but the security rules are blocking it. 
                Since you provided a manual Firebase configuration, you must update your Firestore Security Rules in the Firebase Console.
              </p>
              <div className="text-left bg-black/40 rounded-xl p-4 border border-white/5 mb-6">
                <h3 className="text-white font-semibold mb-2 text-sm">How to fix this:</h3>
                <ol className="text-zinc-400 text-sm space-y-2 list-decimal list-inside">
                  <li>Go to the Firebase Console</li>
                  <li>Select your project</li>
                  <li>Click on <strong>Firestore Database</strong></li>
                  <li>Go to the <strong>Rules</strong> tab</li>
                  <li>Paste the security rules provided earlier</li>
                  <li>Click <strong>Publish</strong></li>
                </ol>
              </div>
              <button 
                onClick={() => window.location.reload()}
                className="w-full py-3 bg-white text-black font-bold rounded-xl hover:bg-gray-200 transition-colors"
              >
                I've updated the rules, reload app
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center shadow-2xl">
            <div className="w-32 h-32 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6 ring-1 ring-red-500/20">
              <AlertTriangle className="text-red-500 w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Something went wrong</h2>
            <p className="text-zinc-400 mb-6 text-sm">
              An unexpected error occurred in the application.
            </p>
            <div className="bg-black/40 rounded-xl p-4 border border-white/5 mb-6 overflow-auto max-h-48 text-left">
              <pre className="text-red-400 text-xs font-mono whitespace-pre-wrap">
                {this.state.error?.toString()}
              </pre>
            </div>
            <button 
              onClick={() => window.location.reload()}
              className="w-full py-3 bg-white text-black font-bold rounded-xl hover:bg-gray-200 transition-colors"
            >
              Reload Application
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
